/**
 * Chinese wording for the expert screens (设计方案 §4.1 §4.2 §5.2–§5.5).
 * One source of truth so the workbench and the arbitration panel never drift.
 */
import { FINALS, FINAL_CODES, INITIALS, INITIAL_CODES, MARK_SEVERITY, RUBRIC_DIMENSIONS, SCORE_ANCHORS as SHARED_SCORE_ANCHORS } from "@xinghai/shared";
import type { Domain, MarkType, Task } from "@xinghai/shared";

/** §4.1 六类 mark. */
export const MARK_LABEL: Record<MarkType, string> = {
  correct: "正确",
  substitution: "替换",
  deletion: "省略",
  insertion: "插入",
  accented: "带口音",
  unrecognizable: "无法辨识",
};

/** Keyboard order 1–6 while a character card has focus. */
export const MARK_ORDER: readonly MarkType[] = [
  "correct",
  "substitution",
  "deletion",
  "insertion",
  "accented",
  "unrecognizable",
];

/** Every dimension key the expert screens can show, in Chinese. */
export const DIMENSION_LABEL: Record<string, string> = {
  // 字级 · 朗读
  initial: "声母",
  final: "韵母",
  tone: "声调",
  // 字级 · 歌唱 (also the singing utterance rubric — same three words)
  onset: "字头",
  nucleus: "字腹",
  coda: "字尾",
  // 句级 · 朗读
  accuracy: "准确度",
  completeness: "完整度",
  fluency: "流畅度",
  prosodic: "表达",
  total: "总体",
  // 句级 · 歌唱
  intelligibility: "可懂度",
  rhythm: "节奏",
  // §5.5
  listening_effort: "费力度",
  accentedness: "口音度",
  // 离散维度
  mark_type: "标记类型",
  realized: "实现音素",
  realized_tone: "实现调类",
  zhe_realized: "实现辙位",
};

export function dimensionLabel(key: string): string {
  return DIMENSION_LABEL[key] ?? key;
}

/** Continuous character dimensions shown per domain (红线 14.1). */
export const CHAR_DIMS: Record<Domain, readonly CharDimKey[]> = {
  reading: ["initial", "final", "tone"],
  singing: ["onset", "nucleus", "coda"],
};

export type CharDimKey = "initial" | "final" | "tone" | "onset" | "nucleus" | "coda";

/** Utterance rubric rows: [payload key, label]. */
export const UTTERANCE_ROWS: Record<Domain, ReadonlyArray<readonly [string, string]>> = {
  reading: [
    ["accuracy", "准确度"],
    ["completeness", "完整度"],
    ["fluency", "流畅度"],
    ["prosodic", "表达"],
    ["total", "总体"],
  ],
  singing: [
    ["onset", "字头"],
    ["nucleus", "字腹"],
    ["coda", "字尾"],
    ["intelligibility", "可懂度"],
    ["rhythm", "节奏"],
  ],
};

export const DOMAIN_LABEL: Record<Domain, string> = {
  reading: "朗读",
  singing: "歌唱",
};

/** Shared anchors for every continuous score. 0 is a valid score; blank means not labelled. */
export const SCORE_ANCHORS = SHARED_SCORE_ANCHORS.map((a, i) => ({ ...a, label: `${i} ${a.label}` }));

export const RUBRIC_GUIDE: Record<Domain, ReadonlyArray<{ key: string; label: string; anchor: string }>> = {
  reading: RUBRIC_DIMENSIONS.reading.map(([key, label, anchor]) => ({ key, label, anchor })),
  singing: RUBRIC_DIMENSIONS.singing.map(([key, label, anchor]) => ({ key, label, anchor })),
};

/** Expert-facing task names (internal codes are fine on the expert side). */
export const TASK_LABEL_EXPERT: Record<Task, string> = {
  T1_reference: "示范朗读",
  T1b_reference_singing: "示范演唱",
  T2_diagnostic: "单字",
  T3_tongue_twister: "绕口令",
  T4_passage: "段落",
  T5_lyric_reading: "歌词朗读",
  T6_singing: "演唱",
  T7_perception: "听辨",
};

/** 实现调类 0–4 (0 = 轻声). */
export const TONE_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 0, label: "轻声" },
];

export interface PhoneOption {
  value: string;
  label: string;
}

/** `ZH_I_SH` → `sh · ZH_I_SH`. */
export const INITIAL_OPTIONS: PhoneOption[] = INITIALS.map((i) => ({
  value: INITIAL_CODES[i],
  label: `${i} · ${INITIAL_CODES[i]}`,
}));

export const FINAL_OPTIONS: PhoneOption[] = FINALS.map((f) => ({
  value: FINAL_CODES[f],
  label: `${f === "v" ? "ü" : f} · ${FINAL_CODES[f]}`,
}));

/** §4.1 severity table — the defaults auto-consistency applies. */
export const SEVERITY = MARK_SEVERITY;

/** `ZH_I_SH` → `sh`, for compact display in the arbitration table. */
export function phoneLabel(code: string | null): string {
  if (!code) return "—";
  const i = INITIAL_OPTIONS.find((o) => code.startsWith(o.value));
  if (i && code === i.value) return i.label;
  const f = FINAL_OPTIONS.find((o) => code === o.value);
  if (f) return f.label;
  return code;
}

/** Human value for one side of a conflict row. */
export function conflictValueLabel(dimension: string, value: number | string | null): string {
  if (value === null || value === undefined) return "未评定";
  if (dimension === "mark_type" && typeof value === "string") {
    return MARK_LABEL[value as MarkType] ?? value;
  }
  if (dimension === "realized_tone") {
    const n = Number(value);
    return n === 0 ? "轻声" : String(n);
  }
  if (dimension === "realized" && typeof value === "string") return phoneLabel(value);
  if (typeof value === "number") return value.toFixed(2);
  return String(value);
}

/** Which control the adjudicator gets for a 自定值. */
export type DimensionKind = "score" | "mark" | "phone" | "tone" | "zhe";

export function dimensionKind(dimension: string): DimensionKind {
  switch (dimension) {
    case "mark_type":
      return "mark";
    case "realized":
      return "phone";
    case "realized_tone":
      return "tone";
    case "zhe_realized":
      return "zhe";
    default:
      return "score";
  }
}

/** `2:03` / `8 秒` — same compact form the student list uses. */
export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return "—";
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s} 秒`;
}

/** 等待时长 for the arbitration queue. */
export function formatWaiting(since: string | null | undefined, now = Date.now()): string {
  if (!since) return "—";
  const t = Date.parse(since);
  if (Number.isNaN(t)) return "—";
  const mins = Math.max(0, Math.round((now - t) / 60_000));
  if (mins < 60) return `${mins} 分钟`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时`;
  return `${Math.floor(hours / 24)} 天`;
}

/** 「剩约 1 小时 10 分」 — coarse, human, never seconds. */
export function formatRemaining(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms <= 0) return "—";
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return "不到 1 分钟";
  if (mins < 60) return `${mins} 分钟`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} 小时` : `${h} 小时 ${m} 分`;
}

/** 平均每条用时 — seconds below a minute, else m 分 s 秒. */
export function formatAverage(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms <= 0) return "—";
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs} 秒`;
  return `${Math.floor(secs / 60)} 分 ${String(secs % 60).padStart(2, "0")} 秒`;
}

/** `mm:ss` elapsed timer for the workbench. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** Stars 1–5 → [0,1] (§5.2/§5.3 normalisation). `null` stays unlabeled. */
export function starsToScore(stars: number | null): number | null {
  return stars === null ? null : (stars - 1) / 4;
}

/** Inverse of {@link starsToScore}, for restoring a draft / server judgment. */
export function scoreToStars(score: number | null | undefined): number | null {
  if (score === null || score === undefined) return null;
  return Math.min(5, Math.max(1, Math.round(score * 4) + 1));
}
